import { Component, Input, OnInit, OnChanges, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { environment } from 'src/environments/environment';
import { DatePipe } from '@angular/common';
import { MessageService } from 'primeng/api';
import { TalentPoolService } from 'src/app/services/talent-pool.service';
import { JobServicesService } from 'src/app/services/job-services.service';

@Component({
    selector: 'app-candidate-basic-info',
    templateUrl: './candidate-basic-info.component.html',
    styleUrls: ['./candidate-basic-info.component.css'],
    providers: [DatePipe, MessageService],
    standalone: false
})
export class CandidateBasicInfoComponent implements OnInit, OnChanges {

  @Input() selectedUser: any;
  @Output() eventClicked = new EventEmitter<{ eventId: string, candidateId: string }>();
  @Output() switchToCandidateInfo = new EventEmitter<any>();
  activeTab: 'personal' | 'events' = 'personal';
  personalInfo: Array<any> = [];
  educationQualification: Array<any> = [];
  experience: Array<any> = [];
  eventDetails: Array<{ field: string, details: Array<{ field: string, value: string }>, eventId: string }> = [];
  usersAllEvent: Array<any> = [];
  isLoadingEventDetails: boolean = false;
  eventDetailsLoaded: boolean = false;

  pdfSrc: string = '';
  signedProfileUrl: string = '';
  dateFormat: string = 'yyyy-MM-dd';
  isLoadingResume: boolean = false;
  resumeFileType: string = 'pdf';

  constructor(
    private cdr: ChangeDetectorRef,
    private datePipe: DatePipe,
    private messageService: MessageService,
    private talentPoolService: TalentPoolService,
    private jobService: JobServicesService
  ) { }

  ngOnInit(): void {
    this.dateFormat = localStorage.getItem('company-details') ? JSON.parse(localStorage.getItem('company-details') || '{}').company_settings?.date_format : '';
  }

  ngOnChanges(): void {
    if (!this.selectedUser) {
      return;
    }
    this.loadUserData();
    this.loadEventDetails();

    // Fetch detailed event details and resume on sidebar open
    this.fetchDetailedEventDetails();

    // Set resume from selectedUser data as fallback
    if (this.selectedUser?.resume) {
      const resumeUrl = this.selectedUser.resume;
      this.pdfSrc = resumeUrl;
      this.resumeFileType = this.getFileTypeFromUrl(resumeUrl);
    }

    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }

  private loadUserData() {
    const format = this.dateFormat || 'yyyy-MM-dd';
    this.personalInfo = [
      { field: "Gender", value: this.formatEducationValue(this.selectedUser?.gender ?? '-') },
      { field: "Date of Birth", value: this.selectedUser?.date_of_birth ? this.datePipe.transform(this.selectedUser.date_of_birth, format) : '-' },
      { field: "Address", value: this.selectedUser?.currentAddress ?? '-' },
    ];

    this.educationQualification = [
      { field: "Degree", value: this.selectedUser?.degreeDetails?.[0]?.degree ?? '-' },
      { field: "Department", value: this.selectedUser?.degreeDetails?.[0]?.specialization ?? '-' },
      { field: "Batch", value: this.selectedUser?.graduationYear ?? '-' },
    ];

    this.signedProfileUrl = this.selectedUser?.profilePic ?? '';
    if (this.selectedUser?.resume) {
      const resumeUrl = this.selectedUser.resume;
      this.pdfSrc = resumeUrl;
      this.resumeFileType = this.getFileTypeFromUrl(resumeUrl);
    } else {
      this.pdfSrc = '';
      this.resumeFileType = 'pdf';
    }

    this.loadEventDetails();
  }

  private loadEventDetails() {
    if (this.eventDetailsLoaded) {
      return;
    }
    if (this.selectedUser?.events?.length) {
      this.eventDetails = this.selectedUser.events.map((event: any) => ({
        field: event.event_name,
        details: [
          { field: "Stage Name", value: event.stage_name ?? '-' },
          { field: "Status", value: event.current_status ?? '-' },
          { field: "Joined Date", value: event.joined_date ? this.datePipe.transform(event.joined_date, this.dateFormat) : '-' },
        ],
        eventId: event.event_uuid
      }));
    }
  }

  fetchDetailedEventDetails() {
    const candidateId = this.selectedUser?.uuid || this.selectedUser?.id;
    if (!candidateId || this.isLoadingEventDetails || this.eventDetailsLoaded) {
      return;
    }

    this.isLoadingEventDetails = true;
    this.talentPoolService.getCandidateEventDetailsFastApi(candidateId).subscribe({
      next: (response: any) => {
        const events = response?.body || response || [];
        if (Array.isArray(events) && events.length) {
          this.eventDetails = events.map((event: any) => ({
            field: event.event_name || 'Event',
            details: [
              { field: "Stage Name", value: event.stage_name || '-' },
              { field: "Status", value: event.current_status || '-' },
              { field: "Joined Date", value: event.joined_date ? this.datePipe.transform(event.joined_date, this.dateFormat) : '-' },
            ],
            eventId: event.event_uuid
          }));

          // Fetch resume using first event's UUID
          if (events[0]?.event_uuid) {
            this.fetchResumeUrl(events[0].event_uuid);
          }
        }
        this.eventDetailsLoaded = true;
        this.isLoadingEventDetails = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.isLoadingEventDetails = false;
        this.messageService.add({
          severity: 'warn',
          summary: 'Event Details',
          detail: 'Unable to load detailed event information.',
          life: 3000
        });
        this.cdr.detectChanges();
      }
    });
  }

  fetchResumeUrl(eventId: string) {
    const candidateId = this.selectedUser?.uuid || this.selectedUser?.id;
    if (!candidateId || !eventId) {
      return;
    }

    this.isLoadingResume = true;
    this.pdfSrc = '';

    this.jobService.getCandidateResume(eventId, candidateId).subscribe({
      next: (response: any) => {
        const res = response?.body ?? response;
        if (res && res.resume) {
          const resumeUrl = res.resume.toString().trim();
          this.pdfSrc = resumeUrl;
          this.resumeFileType = this.getFileTypeFromUrl(resumeUrl);
        } else {
          this.pdfSrc = '';
          this.resumeFileType = 'pdf';
        }
        this.isLoadingResume = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.pdfSrc = '';
        this.resumeFileType = 'pdf';
        this.isLoadingResume = false;
        this.cdr.detectChanges();
      }
    });
  }

  copyToClipboard(text: string, label: string): void {
    if (!text) return;

    console.log('Copy to clipboard called:', { text, label });

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        console.log('Clipboard API success');
        this.messageService.add({
          severity: 'success',
          summary: 'Copied!',
          detail: `${label} copied to clipboard`,
          life: 2000
        });
      }).catch((err) => {
        console.error('Clipboard API failed:', err);
        this.fallbackCopyToClipboard(text, label);
      });
    } else {
      console.log('Using fallback copy method');
      this.fallbackCopyToClipboard(text, label);
    }
  }

  private fallbackCopyToClipboard(text: string, label: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.messageService.add({
          severity: 'success',
          summary: 'Copied!',
          detail: `${label} copied to clipboard`,
          life: 2000
        });
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Copy Failed',
          detail: 'Please copy manually: ' + text,
          life: 3000
        });
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      this.messageService.add({
        severity: 'error',
        summary: 'Copy Failed',
        detail: 'Please copy manually: ' + text,
        life: 3000
      });
    } finally {
      document.body.removeChild(textArea);
    }
  }

  onEventClick(eventId: string, candidateId: string) {
    if (this.selectedUser && !this.selectedUser.name) {
      this.selectedUser.name = `${this.selectedUser.first_name ?? ''} ${this.selectedUser.last_name ?? ''}`.trim();
    }
    const userData = { ...this.selectedUser };
    if (!userData.id && userData.uuid) {
      userData.id = userData.uuid;
    }
    this.eventClicked.emit({ eventId, candidateId });
    this.switchToCandidateInfo.emit({ ...userData, selectedEventId: eventId });
  }
  onEventsTabClick() {
    this.activeTab = 'events';
    this.fetchDetailedEventDetails();
  }
  hasValidPhoto(candidate: any): boolean {
    return candidate?.photo &&
      candidate.photo.trim() !== '' &&
      candidate.photo !== 'null' &&
      candidate.photo !== null;
  }

  onImageError(event: any, candidate: any): void {
    event.target.style.display = 'none';
    const initialsElement = event.target.nextElementSibling;
    if (initialsElement) {
      initialsElement.style.display = 'flex';
    }
    candidate._photoError = true;
  }

  onImageLoad(event: any): void {
    event.target.style.display = 'block';
    const initialsElement = event.target.nextElementSibling;
    if (initialsElement) {
      initialsElement.style.display = 'none';
    }
  }

  getInitials(fullName: string): string {
    if (!fullName) return 'UN';

    const names = fullName.trim().split(' ');
    if (names.length === 1) {
      return names[0].charAt(0).toUpperCase();
    }

    const firstInitial = names[0].charAt(0).toUpperCase();
    const lastInitial = names[names.length - 1].charAt(0).toUpperCase();
    return firstInitial + lastInitial;
  }

  getAvatarColor(name: string): string {
    const colors = [
      '#FF6B35', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57',
      '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3', '#FF9F43',
      '#EE5A24', '#009432', '#0652DD', '#9980FA', '#833471',
      '#F79F1F', '#A3CB38', '#1289A7', '#D63031', '#74B9FF'
    ];

    if (!name) return colors[0];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  }

  // Get status badge class based on status value
  getStatusClass(status: string): string {
    if (!status) return 'status-default';

    const statusLower = status.toLowerCase().replace(/\s+/g, '-');

    if (statusLower.includes('shortlist')) return 'status-badge status-shortlist';
    if (statusLower.includes('slot') || statusLower.includes('send')) return 'status-badge status-slot-send';
    if (statusLower.includes('applied')) return 'status-badge status-applied';
    if (statusLower.includes('reject')) return 'status-badge status-rejected';
    if (statusLower.includes('select')) return 'status-badge status-selected';

    return 'status-badge status-default';
  }

  // Format education value - remove underscores and capitalize
  formatEducationValue(value: string): string {
    if (!value) return '-';

    return value
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Open resume in new tab
  openResumeInNewTab(): void {
    if (this.pdfSrc) {
      window.open(this.pdfSrc, '_blank');
    }
  }

  getFullName(): string {
    const firstName = this.selectedUser?.first_name || '';
    const lastName = this.selectedUser?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();

    if (!fullName && this.selectedUser?.email) {
      return this.selectedUser.email.split('@')[0];
    }

    return fullName || 'Unknown';
  }

  getFileTypeFromUrl(url: string): string {
    if (!url) return 'pdf';
    const urlLower = url.toLowerCase();
    if (urlLower.includes('.docx')) return 'docx';
    if (urlLower.includes('.doc')) return 'doc';
    if (urlLower.includes('.pdf')) return 'pdf';
    return 'pdf';
  }

  getResumeViewerUrl(url: string): string {
    const fileType = this.getFileTypeFromUrl(url);
    if (fileType === 'docx' || fileType === 'doc') {
      // Use Microsoft Office Online Viewer
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    }
    return url;
  }
}
